import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  agentReleaseSchema,
  agentSchema,
  createAgentResponseSchema,
  messageKey,
  testTcpResponseSchema,
  type Agent,
  type AgentAsset,
  type AgentRelease,
  type CreateAgentResponse,
  type TestTcpResponse,
  type UpdateAgent,
} from '@netdeck/contracts';
import { z } from 'zod';
import { API_BASE, ApiError, apiRequest, refreshAccessToken } from '../../shared/api/client';
import { authStore } from '../../shared/api/auth-store';

export const agentsKey = ['agents'] as const;

/**
 * Агенты организации.
 *
 * Список обновляется каждые 5 секунд: статус агента — то, ради чего на этот
 * экран заходят, и узнавать об отключении площадки по факту перезагрузки
 * страницы никуда не годится.
 */
export function useAgents() {
  return useQuery({
    queryKey: agentsKey,
    queryFn: () => apiRequest('/agents', z.array(agentSchema)),
    refetchInterval: 5_000,
  });
}

/**
 * Заведение агента сразу с его площадками (ADR 033).
 *
 * Площадки списком, а не по одной следом за созданием: пара «создать, потом
 * перенести» оставляла бы в журнале переезд, которого не было, и разъезжалась
 * бы при отказе на втором шаге — агент оставался бы заведённым, но пустым.
 */
export function useCreateAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ siteIds, name }: { siteIds: string[]; name: string }) =>
      apiRequest('/agents', createAgentResponseSchema, {
        method: 'POST',
        body: { name, siteIds },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: agentsKey }),
  });
}

/**
 * Правка агента: название, состав площадок, автообновление.
 *
 * Смена состава меняет не подпись, а обязанности: агент раздаёт мониторы
 * своих площадок и служит маршрутом к их узлам. Соединение при этом
 * не рвётся — сервер переносит его вместе с записью.
 */
export function useUpdateAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, input }: { agentId: string; input: UpdateAgent }) =>
      apiRequest<Agent>(`/agents/${agentId}`, agentSchema, { method: 'PATCH', body: input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: agentsKey }),
  });
}

export function useRotateToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agentId: string) =>
      apiRequest(`/agents/${agentId}/rotate-token`, z.object({ enrollmentToken: z.string() }), {
        method: 'POST',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: agentsKey }),
  });
}

export function useDeleteAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agentId: string) =>
      apiRequest(`/agents/${agentId}`, z.unknown(), {
        method: 'DELETE',
        acceptStatuses: [204],
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: agentsKey }),
  });
}

/**
 * Доступная сборка агента (§11, фаза 9, задача 4).
 *
 * Спрашивается редко: каталог выпусков меняет человек, кладя туда файлы,
 * и опрашивать его каждые пять секунд незачем. Обновляется при возврате
 * на вкладку — этого хватает, чтобы увидеть только что выложенный выпуск.
 */
export function useAgentRelease() {
  return useQuery({
    queryKey: ['agents', 'release'],
    queryFn: () => apiRequest('/agents/release', agentReleaseSchema),
    staleTime: 60_000,
  });
}

/**
 * Обновить агента сейчас.
 *
 * Отдельно от флага автообновления: администратор может держать
 * автообновление выключенным и обновлять площадки по одной в удобное время.
 */
export function useUpdateAgentNow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agentId: string) =>
      apiRequest(`/agents/${agentId}/update`, z.unknown(), {
        method: 'POST',
        acceptStatuses: [202],
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: agentsKey }),
  });
}

export function useTestTcp() {
  return useMutation({
    mutationFn: ({ agentId, host, port }: { agentId: string; host: string; port: number }) =>
      apiRequest(`/agents/${agentId}/test-tcp`, testTcpResponseSchema, {
        method: 'POST',
        body: { host, port },
      }),
  });
}

/**
 * Скачать сборку агента или скрипт установки.
 *
 * Запросом, а не ссылкой: access-токен живёт в памяти вкладки и уходит
 * заголовком, а обычный `<a href>` отправился бы на сервер без него —
 * и вместо файла администратор получил бы отказ в доступе.
 *
 * Файл собирается в память целиком. Для §13 с его пределом в 20 МБ этого
 * достаточно, а потоковая запись на диск, как в файловом менеджере, нужна
 * там, где ходят гигабайты.
 */
export async function downloadAgentAsset(asset: string): Promise<string> {
  const request = async (): Promise<Response> =>
    fetch(`${API_BASE}/agents/release/file?asset=${encodeURIComponent(asset)}`, {
      credentials: 'include',
      headers: { ...(authStore.token ? { authorization: `Bearer ${authStore.token}` } : {}) },
    });

  let response = await request();

  // Скачивание нажимают редко и часто после долгого чтения экрана: токен
  // к этому моменту успевает истечь, и первый же отказ должен приводить
  // к обновлению токена, а не к сообщению об ошибке.
  if (response.status === 401 && (await refreshAccessToken())) {
    response = await request();
  }

  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    const body = (payload as { error?: { code?: string; message?: string } } | null)?.error;

    throw new ApiError(response.status, {
      code: body?.code ?? 'UNKNOWN',
      message: body?.message ?? messageKey('errors.code.byStatus', { status: response.status }),
    });
  }

  const name = fileName(response.headers.get('content-disposition'), asset);
  const url = URL.createObjectURL(await response.blob());

  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.click();
  } finally {
    // Не сразу: браузеру нужен живой адрес на время начала загрузки.
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 60_000);
  }

  return name;
}

/** Имя файла из заголовка; запасное — из ключа скачивания. */
function fileName(disposition: string | null, asset: string): string {
  const match = /filename="([^"]+)"/.exec(disposition ?? '');

  return match?.[1] ?? asset.replace('/', '-');
}

export type { Agent, AgentAsset, AgentRelease, CreateAgentResponse, TestTcpResponse };
